import { AlertCircleIcon, CheckCircleIcon } from "lucide-react";
import { Badge, Box, Callout, Flex, Text, TextField } from "@radix-ui/themes";
import { useOAuthForm } from "../OAuthFormContext";
import { getProviderExtension } from "../providers/registry";
import { supportsNative } from "../shape";

const sectionStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: 16,
  borderRadius: 8,
  border: "1px solid var(--gray-5)",
  backgroundColor: "var(--gray-2)",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

export function OAuthConfigForm() {
  const state = useOAuthForm();
  const { definition } = state;
  const ext = getProviderExtension(definition.provider);
  const isApple = definition.provider === "apple";
  const showNative = supportsNative(definition);

  const liveNative = state.nativeAudiencesText
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean).length > 0;

  // Apple's .p8 path needs PEM + Key + Team in lieu of a client secret.
  const liveWeb =
    !!state.webClientId.trim() &&
    (isApple && state.appleWebMethod === "p8"
      ? (!!state.applePrivateKey.trim() || state.hasExistingApplePrivateKey) &&
        !!state.appleKeyId.trim() &&
        !!state.appleTeamId.trim()
      : !!state.webClientSecret.trim() || state.hasExistingWebSecret);

  const hideGenericClientSecret =
    ext.hideGenericClientSecretWhen?.(state) ?? false;
  const showWebSecretBody = ext.webSecretBodyShownWhen?.(state) ?? false;
  const WebSecretMethod = ext.WebSecretMethod;
  const WebSecretBody = ext.WebSecretBody;
  const Advanced = ext.Advanced;

  const labels = ext.labels ?? {};
  const nativeAudienceLabel = labels.nativeAudienceLabel ?? "Client IDs";
  const nativeAudiencePlaceholder =
    labels.nativeAudiencePlaceholder ?? "your-app-client-id";
  const nativeAudienceHint =
    labels.nativeAudienceHint ??
    "Use the OAuth client ID(s) issued for your mobile app.";
  const webClientIdLabel = labels.webClientIdLabel ?? "Client ID";
  const webClientIdPlaceholder =
    labels.webClientIdPlaceholder ?? "your-web-client-id";

  return (
    <>
      <Flex
        gap="4"
        align="stretch"
        direction={{ initial: "column", md: "row" }}
      >
        {showNative && (
          <Box style={sectionStyle}>
            <Flex justify="between" align="center">
              <Text size="3" weight="bold">
                Native sign-in
              </Text>
              {liveNative ? (
                <Badge color="green" variant="soft" size="1">
                  <CheckCircleIcon size={12} style={{ marginRight: 4 }} />
                  Configured
                </Badge>
              ) : (
                <Badge color="gray" variant="soft" size="1">
                  Off
                </Badge>
              )}
            </Flex>
            <Text size="1" color="gray" style={{ display: "block" }}>
              Mobile apps using the {definition.display_name} SDK (Flutter /
              iOS / Android). The app gets an identity token from the SDK and
              posts it to Anythink; we verify the signature against{" "}
              {definition.display_name}'s public keys.{" "}
              <strong>No client secret needed</strong> — just list the
              audiences your app will present.
            </Text>
            <Box>
              <Text size="2" weight="medium" mb="1">
                {nativeAudienceLabel}
              </Text>
              <TextField.Root
                value={state.nativeAudiencesText}
                onChange={(e) => state.setNativeAudiencesText(e.target.value)}
                placeholder={nativeAudiencePlaceholder}
              />
              <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
                Comma-separated. {nativeAudienceHint}
              </Text>
            </Box>
          </Box>
        )}

        <Box style={sectionStyle}>
          <Flex justify="between" align="center">
            <Text size="3" weight="bold">
              Web sign-in
            </Text>
            {liveWeb ? (
              <Badge color="green" variant="soft" size="1">
                <CheckCircleIcon size={12} style={{ marginRight: 4 }} />
                Configured
              </Badge>
            ) : (
              <Badge color="gray" variant="soft" size="1">
                Off
              </Badge>
            )}
          </Flex>
          <Text size="1" color="gray" style={{ display: "block" }}>
            Browser button on your website (OAuth redirect to the callback URL
            above). Needs a client secret so Anythink can exchange the auth code
            for tokens server-side.
          </Text>

          <Box>
            <Text size="2" weight="medium" mb="1">
              {webClientIdLabel}
            </Text>
            <TextField.Root
              value={state.webClientId}
              onChange={(e) => state.setWebClientId(e.target.value)}
              placeholder={webClientIdPlaceholder}
            />
          </Box>

          {WebSecretMethod && <WebSecretMethod />}

          {showWebSecretBody && WebSecretBody ? <WebSecretBody /> : null}

          {!hideGenericClientSecret && (
            <Box>
              <Flex align="center" gap="2" mb="1">
                <Text size="2" weight="medium">
                  Client secret
                </Text>
                {state.hasExistingWebSecret && (
                  <Badge color="green" variant="soft" size="1">
                    <CheckCircleIcon size={12} style={{ marginRight: 4 }} />
                    Stored
                  </Badge>
                )}
              </Flex>
              <TextField.Root
                type="password"
                value={state.webClientSecret}
                onChange={(e) => state.setWebClientSecret(e.target.value)}
                placeholder={
                  state.hasExistingWebSecret
                    ? "Paste a new value here to replace — leave blank to keep using the stored one."
                    : "Enter Client Secret"
                }
              />
            </Box>
          )}
        </Box>
      </Flex>

      {Advanced && <Advanced />}

      {showNative && !liveNative && !liveWeb && (
        <Callout.Root size="1" color="amber">
          <Callout.Icon>
            <AlertCircleIcon size={14} />
          </Callout.Icon>
          <Callout.Text>
            At least one flow needs to be configured for users to sign in with{" "}
            {definition.display_name}.
          </Callout.Text>
        </Callout.Root>
      )}
    </>
  );
}
